import { useField } from "formik";
import type { ComponentProps, ReactNode } from "react";
import { t } from "ttag";

import type {
  CollectionAuthorityLevelDisplayProps,
  CollectionAuthorityLevelIcon as CollectionAuthorityLevelIconComponent,
} from "metabase/plugins";
import { Flex, Group, Icon, Menu, Radio, Text } from "metabase/ui";
import type { Collection, CollectionAuthorityLevel } from "metabase-types/api";

import { OFFICIAL_COLLECTION } from "../../constants";
import { isOfficialCollection } from "../../utils";

type CollectionAuthorityLevelIconProps =
  ComponentProps<CollectionAuthorityLevelIconComponent>;

/** The Official badge of a collection; nothing for a regular collection. */
export function CollectionAuthorityLevelIcon({
  collection,
  tooltip = "default",
  archived,
  ...iconProps
}: CollectionAuthorityLevelIconProps) {
  if (!isOfficialCollection(collection)) {
    return null;
  }

  return (
    <Icon
      {...iconProps}
      name={OFFICIAL_COLLECTION.icon}
      c={archived ? "text-disabled" : OFFICIAL_COLLECTION.color}
      tooltip={OFFICIAL_COLLECTION.tooltips?.[tooltip]}
    />
  );
}

/** "Official collection" in a collection's info sidebar. */
export function CollectionAuthorityLevelDisplay({
  collection,
}: CollectionAuthorityLevelDisplayProps) {
  if (!isOfficialCollection(collection)) {
    return null;
  }

  return (
    <Flex align="center" gap="sm">
      <Icon name={OFFICIAL_COLLECTION.icon} c={OFFICIAL_COLLECTION.color} />
      <Text>{t`Official collection`}</Text>
    </Flex>
  );
}

const REGULAR_VALUE = "regular";
const OFFICIAL_VALUE = "official";

type FormCollectionAuthorityLevelPickerProps = {
  name: string;
  title?: string;
};

/** The Regular / Official choice of the new-collection form, for admins. */
export function FormCollectionAuthorityLevelPicker({
  name,
  title = t`Collection type`,
}: FormCollectionAuthorityLevelPickerProps) {
  const [{ value }, , { setValue }] = useField<
    CollectionAuthorityLevel | undefined
  >(name);

  return (
    <Radio.Group
      label={title}
      value={value === OFFICIAL_VALUE ? OFFICIAL_VALUE : REGULAR_VALUE}
      onChange={(newValue) =>
        setValue(newValue === OFFICIAL_VALUE ? OFFICIAL_VALUE : null)
      }
    >
      <Group gap="lg" mt="sm">
        <Radio value={REGULAR_VALUE} label={t`Regular`} />
        <Radio value={OFFICIAL_VALUE} label={t`Official`} />
      </Group>
    </Radio.Group>
  );
}

/** "Make collection official" or "Remove Official badge" in the collection menu. */
export function getAuthorityLevelMenuItems(
  collection: Collection,
  onUpdate: (collection: Collection, values: Partial<Collection>) => void,
): ReactNode[] {
  if (isOfficialCollection(collection)) {
    return [
      <Menu.Item
        key="collection-authority-level"
        leftSection={<Icon name="official_collection" />}
        onClick={() => onUpdate(collection, { authority_level: null })}
      >
        {t`Remove Official badge`}
      </Menu.Item>,
    ];
  }

  return [
    <Menu.Item
      key="collection-authority-level"
      leftSection={<Icon name="official_collection" />}
      onClick={() => onUpdate(collection, { authority_level: "official" })}
    >
      {t`Make collection official`}
    </Menu.Item>,
  ];
}
